import React, { useState, useEffect, forwardRef } from "react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import service from "../../../store/services/user.service";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { NO_RESULTS_FOUND } from "../../../store/constants";

const CustomSelect = forwardRef((props, ref) => {
  const {
    label,
    selectWd,
    onChange,
    searchText,
    defaultVal,
    forceRenderDefaultVal = true,
    options,
    multiselect,
    errorMessage,
    SelectText,
    CustomClass,
    creatable,
    setCreated,
    createOptionRequest,
    createOptionFieldName = "manager",
    optionMessage,
    isRequired,
    handleInputChange = null,
    handleBlur,
    displayErrorMessage = true,
    loading = false,
    isDatatable = false,
    onKeyDown = null,
    isDisabled = false,
    menuPortalTarget = document.body,
    isClearable = true,
  } = props;

  const [value, setValue] = useState(defaultVal);
  const [allOptions, setAllOptions] = useState(options);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [valueChange, setValueChange] = useState(null);
  const selectRef = React.useRef(null);

  useEffect(() => {
    setMessage(optionMessage);
  }, [optionMessage]);

  const handleChange = (e) => {
    if (multiselect) {
      const values = e.map((val) => val.value);
      setValue(e);
      onChange(values);
    } else {
      setValue(e);
      onChange(e);
    }
  };

  const handleKeyDownEvent = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (onKeyDown) {
        onKeyDown(e);
      }
    }
  };

  const handleFocus = () => {
    searchText(null);
    setMessage("Start typing...");
    setValue((prev) => prev);

    // Open menu on focus
    if (selectRef.current) {
      setTimeout(() => {
        if (selectRef.current.select && selectRef.current.select.openMenu) {
          selectRef.current.select.openMenu();
        } else if (selectRef.current.openMenu) {
          selectRef.current.openMenu();
        }
      }, 10);
    }
  };

  // Custom styles to hide the dropdown indicator arrow and separator line
  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      fontFamily: "inherit",
      fontSize: "inherit",
      color: "inherit",
      height: "32px",
      backgroundColor: "none",
      border: "none !important",
      boxShadow: "none !important",
      outline: "none !important",
      "&:focus": {
        boxShadow: "none !important",
        outline: "none !important",
      },
      "&:focus-within": {
        boxShadow: "none !important",
        outline: "none !important",
      },
    }),
    dropdownIndicator: () => ({
      display: "none",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    input: (provided) => ({
      ...provided,
      fontSize: "12px",
      outline: "none !important",
    }),
    option: (provided, state) => ({
      ...provided,
      color: state.isSelected ? "#000" : "#000",
      fontSize: "12px",
      backgroundColor: state.isFocused ? "#E6E6E6" : "white",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#ECE7FD",
      borderRadius: "6px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#564F97",
      fontSize: "12px",
      fontWeight: 500,
      padding: "2px 6px",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#564F97",
      borderRadius: "0 6px 6px 0",
      ":hover": {
        backgroundColor: "#564F97",
        color: "#FFFFFF",
      },
    }),
  };

  useEffect(() => {
    if (forceRenderDefaultVal) {
      setValue(defaultVal);
    }
  }, [defaultVal, forceRenderDefaultVal]);

  const customNoOptionsMessage = () => (message ? message : NO_RESULTS_FOUND);

  const createHandler = (inputValue) => {
    setIsLoading(true);
    const createRequest = createOptionRequest || service.createNewUserRequest;
    createRequest(inputValue).then((res) => {
      if (res) {
        const transformedArray = {
          label: res.name,
          value: res.id,
          image: res.image,
        };
        setCreated(transformedArray);
        setAllOptions((prev) => [...prev, transformedArray]);
        defaultVal
          ? setValue((prev) => [...prev, transformedArray])
          : setValue(transformedArray);
        if (handleInputChange) {
          handleInputChange(transformedArray, createOptionFieldName);
        }
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    setAllOptions(options);
    if (!creatable) {
      if (valueChange?.length > 0) {
        setMessage("No Results");
      } else {
        setMessage(null);
      }
    }
  }, [options]);

  return (
    <>
      <div className={selectWd}>
        {label && (
          <label className={`Input_label`}>
            {label} {isRequired && <span className="Input_staric">*</span>}
          </label>
        )}
        {loading ? (
          <Skeleton height={40} baseColor="#e0e0e0" highlightColor="#ECE7FD" />
        ) : creatable ? (
          <CreatableSelect
            ref={(instance) => {
              // Handle both refs
              if (ref) {
                if (typeof ref === "function") {
                  ref(instance);
                } else {
                  ref.current = instance;
                }
              }
              selectRef.current = instance;
            }}
            styles={customStyles}
            value={value}
            isClearable ={isClearable}
            defaultValue={value}
            onInputChange={(value, actionMeta) => searchText(value, actionMeta)}
            onChange={(value) => handleChange(value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            options={allOptions}
            placeholder={`Search ${
              !label && isRequired
                ? `${SelectText && SelectText} *`
                : SelectText && SelectText
            }`}
            isSearchable
            isMulti={multiselect}
            className={
              !isDatatable
                ? `${
                    errorMessage && "CustomSelectWrapper_error"
                  } ${CustomClass} CustomSelectWrapper`
                : CustomClass
            }
            noOptionsMessage={customNoOptionsMessage}
            isDisabled={isLoading || isDisabled}
            isLoading={isLoading}
            onCreateOption={createHandler}
            menuPortalTarget={document.body}
            onKeyDown={handleKeyDownEvent}
          />
        ) : (
          <Select
            ref={(instance) => {
              // Handle both refs
              if (ref) {
                if (typeof ref === "function") {
                  ref(instance);
                } else {
                  ref.current = instance;
                }
              }
              selectRef.current = instance;
            }}
            styles={customStyles}
            value={value}
            isClearable={isClearable}
            onBlur={handleBlur}
            defaultValue={value}
            onInputChange={(value, actionMeta) => {
              searchText(value, actionMeta);
              setValueChange(value);
            }}
            onChange={(value) => handleChange(value)}
            onFocus={handleFocus}
            options={options}
            placeholder={`Search ${
              !label && isRequired
                ? `${SelectText && SelectText} *`
                : SelectText && SelectText
            }`}
            isSearchable
            isMulti={multiselect}
            className={
              !isDatatable
                ? `${
                    errorMessage && "CustomSelectWrapper_error"
                  } ${CustomClass} CustomSelectWrapper`
                : CustomClass
            }
            noOptionsMessage={customNoOptionsMessage}
            menuPortalTarget={menuPortalTarget}
            onKeyDown={handleKeyDownEvent}
            isDisabled={isLoading || isDisabled}
          />
        )}
        {errorMessage && displayErrorMessage && (
          <p className="Input_errorMessage">{errorMessage}</p>
        )}
      </div>
    </>
  );
});

export default CustomSelect;
